import React from "react";
import { ChevronDown, User, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSuiteContext } from "./SuiteContextProvider";
import { ROLE_HIERARCHY_FE } from "./roleLabels";

/**
 * ContextIndicator - badge SEMPRE visibile in TopBar.
 *
 * Anche quando il Context Engine non ha ancora caricato lo stato (primo
 * render, offline, errore rete), il badge mostra i dati dell'utente
 * autenticato reale come fallback, in modo che il pulsante di cambio
 * contesto sia sempre cliccabile.
 */
export function ContextIndicator({ onClick, compact = false }) {
  const { user } = useAuth();
  const { context, loading } = useSuiteContext();

  // Caso 1: Context Engine caricato → usa i suoi dati.
  if (context) {
    const parts = context.breadcrumb || [];
    return (
      <span
        data-testid="suite-context-indicator"
        className={
          "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors " +
          (context.is_impersonating
            ? "border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
            : "border-suite-neutral-200 bg-white text-suite-neutral-700 hover:bg-suite-neutral-50")
        }
        onClick={onClick}
      >
        {context.is_impersonating ? (
          <Sparkles size={14} className="text-amber-600" />
        ) : (
          <User size={14} className="text-suite-neutral-500" />
        )}
        <span className="hidden items-center gap-1.5 md:inline-flex" data-testid="suite-context-breadcrumb">
          {parts.map((p, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-suite-neutral-300">/</span>}
              <span>{p}</span>
            </React.Fragment>
          ))}
        </span>
        <span className="inline md:hidden">Cambia contesto</span>
        <ChevronDown size={14} className="text-suite-neutral-400" />
      </span>
    );
  }

  // Caso 2: fallback prima che il Context Engine risponda.
  // Mostra il ruolo/utente reale + hint "Cambia contesto".
  const realRoleLabel =
    (user?.role && ROLE_HIERARCHY_FE[user.role]) || "Utente";
  return (
    <span
      data-testid="suite-context-indicator"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-full border border-suite-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-suite-neutral-700 hover:bg-suite-neutral-50"
    >
      {loading ? (
        <Loader2 size={14} className="animate-spin text-suite-neutral-400" />
      ) : (
        <User size={14} className="text-suite-neutral-500" />
      )}
      <span className="hidden md:inline">
        {realRoleLabel} · <span className="text-suite-neutral-400">Cambia contesto</span>
      </span>
      <span className="md:hidden">Cambia contesto</span>
      <ChevronDown size={14} className="text-suite-neutral-400" />
    </span>
  );
}
